import React from 'react';
import { AlertTriangle, Route } from 'lucide-react';

function valueOrDash(value) {
  return value === undefined || value === null || value === '' ? '?' : value;
}

function money(value) {
  if (value === undefined || value === null || value === '') return '?';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `S/ ${numeric.toFixed(2)}` : value;
}

function contextField(label, value, className = '') {
  return (
    <div className={`rounded-lg bg-slate-50 p-2 ${className}`}>
      <p className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 break-words text-xs font-bold text-slate-800">{valueOrDash(value)}</p>
    </div>
  );
}

function nextDeliveriesFrom(routeContext) {
  return routeContext?.next_driver_deliveries || routeContext?.next_deliveries || routeContext?.route?.next_deliveries || [];
}

export function DriverIncidentPanel({ delivery = {}, routeContext = null, latestIntent, incidentResolution = {} }) {
  const isCustomerNotAnswering = latestIntent === 'customer_not_answering';
  const nextDeliveries = nextDeliveriesFrom(routeContext);
  const sequence = delivery.sequence || delivery.route_sequence || routeContext?.current_sequence;
  const storeName = delivery.user_name || delivery.store_name || delivery.store || delivery.provider_name;
  const candidateIds = incidentResolution?.candidate_delivery_point_ids || [];

  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${isCustomerNotAnswering ? 'border-red-300 ring-2 ring-red-100' : 'border-slate-200'}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
            <AlertTriangle className={`h-4 w-4 ${isCustomerNotAnswering ? 'text-red-600' : 'text-orange-600'}`} />
            Driver incident panel
          </h3>
          <p className="text-xs text-slate-500">Operational truth for delivery and route impact.</p>
        </div>
        {isCustomerNotAnswering && (
          <span className="rounded-full bg-red-100 px-2.5 py-1 text-[10px] font-extrabold uppercase text-red-700">
            Cliente no contesta
          </span>
        )}
      </div>

      {!delivery?.found ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-800">
          No hay entrega vinculada. Vincula una entrega antes de ejecutar acciones operativas.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {contextField('Delivery ID', delivery.id)}
          {contextField('Sequence', sequence)}
          {contextField('Driver', delivery.driver_name)}
          {contextField('Store', storeName)}
          {contextField('Recipient', delivery.recipient_name)}
          {contextField('Status', delivery.status)}
          {contextField('Charge/COD', money(delivery.charge))}
          {contextField('District', delivery.district)}
          {contextField('Address', delivery.address, 'col-span-2')}
        </div>
      )}

      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="mb-2 flex items-center gap-2 text-xs font-extrabold text-slate-800">
          <Route className="h-3.5 w-3.5" />
          Next driver deliveries
        </div>
        {nextDeliveries.length > 0 ? (
          <div className="space-y-2">
            {nextDeliveries.slice(0, 5).map((item, index) => (
              <div key={item.id || item.delivery_point_id || index} className="rounded-lg bg-white p-2 text-xs shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-extrabold text-slate-900">#{item.id || item.delivery_point_id}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                    {item.status || 'pending'}
                  </span>
                </div>
                <p className="mt-1 text-slate-600">{item.recipient_name || item.customer_name || 'Cliente'} ? {item.district || 'Distrito pendiente'}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-500">Backend no proporcion? siguientes entregas del conductor.</p>
        )}
      </div>

      {incidentResolution?.status && incidentResolution.status !== 'resolved' && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <p className="font-extrabold uppercase">Requiere vincular entrega</p>
          <p className="mt-1 font-semibold">{incidentResolution.reason || 'No se pudo inferir una ?nica entrega activa.'}</p>
          {candidateIds.length > 0 && (
            <p className="mt-1 font-bold">Candidatas: {candidateIds.join(', ')}</p>
          )}
        </div>
      )}
    </div>
  );
}
